import {
    MAX_FILE_SIZE_BYTES,
    PDF_ACCEPT,
    PDF_SPREADSHEET_ACCEPT,
} from "@/config/constants";
import { cn } from "@/lib/utils";
import { DragEventHandler, ReactNode, useCallback } from "react";
import { type Accept, type FileRejection, useDropzone } from "react-dropzone";
import { toast } from "sonner";

interface DocumentDropZoneProps {
    /** Destination headline shown in the overlay while files are dragged. */
    title: string;
    subtitle: string;
    /** Receives only accepted files; rejections are toasted internally. */
    onDropFiles: (files: File[]) => void;
    /** Suppress the overlay and drops, e.g. while the upload dialog is open. */
    disabled?: boolean;
    /**
     * Ignore drags whose DOM target is outside this element, such as a
     * dialog portaled from inside the wrapped tree (default `false`). The
     * events are skipped, not stopped: a nested dropzone re-checks
     * `isPropagationStopped` after its async file read, and a stopped
     * native event never reaches the document-level guard that keeps the
     * browser from opening the file.
     */
    ignorePortaledDrags?: boolean;
    /**
     * Also accept `.xlsx`/`.csv` (default `false`). Independent of whether
     * the destination is a Knowledge Base folder -- callers on every library
     * upload surface (main + client + KB) pass `true`. The scope and
     * comparison document pickers don't use this component and stay
     * PDF-only. The backend guard is the real enforcement; this is UX.
     */
    allowSpreadsheets?: boolean;
    accept?: Accept;
    maxSize?: number;
    invalidTypeDescription?: string;
    className?: string;
    children: ReactNode;
}

const describeRejection = (
    rejection: FileRejection,
    allowSpreadsheets: boolean,
    maxSize: number,
    invalidTypeDescription?: string
): string => {
    const code = rejection.errors[0]?.code;
    const reason =
        code === "file-too-large"
            ? `over ${Math.round(maxSize / (1024 * 1024))}MB`
            : code === "file-invalid-type"
              ? (invalidTypeDescription ??
                (allowSpreadsheets ? "not a PDF or spreadsheet" : "not a PDF"))
              : "couldn't be read";
    return `${rejection.file.name} (${reason})`;
};

const skipPortaled = (
    handler: DragEventHandler<HTMLElement> | undefined
): DragEventHandler<HTMLElement> | undefined =>
    handler &&
    ((event) => {
        if (event.currentTarget.contains(event.target as Node)) handler(event);
    });

/**
 * Drag-activated drop-zone overlay. Wraps a region (sized by its children)
 * and fades in a primary-tinted overlay with destination copy only while
 * files are dragged over it; it is never a click target. Internal element
 * drags (pragmatic-dnd rows) never activate it: react-dropzone gates
 * `isDragActive` on `dataTransfer.types` containing "Files".
 */
export const DocumentDropZone = ({
    title,
    subtitle,
    onDropFiles,
    disabled = false,
    ignorePortaledDrags = false,
    allowSpreadsheets = false,
    accept,
    maxSize = MAX_FILE_SIZE_BYTES,
    invalidTypeDescription,
    className,
    children,
}: DocumentDropZoneProps) => {
    const onDrop = useCallback(
        (acceptedFiles: File[], fileRejections: FileRejection[]) => {
            if (fileRejections.length > 0) {
                toast.error(
                    fileRejections.length === 1
                        ? "1 file wasn't added"
                        : `${fileRejections.length} files weren't added`,
                    {
                        description: fileRejections
                            .map((r) =>
                                describeRejection(
                                    r,
                                    allowSpreadsheets,
                                    maxSize,
                                    invalidTypeDescription
                                )
                            )
                            .join(", "),
                    }
                );
            }
            if (acceptedFiles.length > 0) onDropFiles(acceptedFiles);
        },
        [onDropFiles, allowSpreadsheets, maxSize, invalidTypeDescription]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        noClick: true,
        noKeyboard: true,
        accept:
            accept ?? (allowSpreadsheets ? PDF_SPREADSHEET_ACCEPT : PDF_ACCEPT),
        maxSize,
        disabled,
    });

    const rootProps = getRootProps();
    if (ignorePortaledDrags) {
        rootProps.onDragEnter = skipPortaled(rootProps.onDragEnter);
        rootProps.onDragOver = skipPortaled(rootProps.onDragOver);
        rootProps.onDrop = skipPortaled(rootProps.onDrop);
    }

    return (
        <div
            {...rootProps}
            data-slot="document-dropzone"
            className={cn("relative", className)}
        >
            <input {...getInputProps()} />
            {children}
            <div
                data-slot="document-dropzone-overlay"
                aria-hidden={!isDragActive}
                className={cn(
                    "pointer-events-none absolute inset-0 z-[80] transition-opacity duration-150 motion-reduce:transition-none",
                    isDragActive ? "opacity-100" : "opacity-0"
                )}
            >
                {isDragActive && (
                    <div
                        className="flex h-full w-full flex-col items-center justify-center gap-1.5 overflow-hidden rounded-xl px-6 text-center"
                        style={{
                            background:
                                "color-mix(in oklch, var(--primary) 5%, color-mix(in oklch, var(--background) 60%, transparent))",
                            // Full-strength primary at the edge fading inward.
                            boxShadow: "inset 0 0 4px 2px var(--primary)",
                        }}
                    >
                        <p className="text-primary text-[19px] font-semibold tracking-tight">
                            {title}
                        </p>
                        <p className="text-foreground/65 text-[13px]">
                            {subtitle}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};
